import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import Button from "@/components/Button";
import DetailsCard, { type SelectOptions } from "@/components/DetailsCard/DetailsCard";
import ListItem from "@/components/ListItem";
import { CardSkeleton } from "@/components/Skeleton";
import { formatDate } from "@/pages/_layouts/jobs/_utils";

function HubCardFooter({
  footerTestId,
  buttonTestId,
  ariaLabel,
  onClick,
  children
}: {
  footerTestId: string;
  buttonTestId: string;
  ariaLabel?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className="-mx-6 -mb-6 mt-auto flex w-[calc(100%+3rem)] items-center justify-center border-t border-sys-brand-surface-container-highest py-2"
      data-testid={footerTestId}
    >
      <Button variant="text" testId={buttonTestId} ariaLabel={ariaLabel} onClick={onClick}>
        {children}
      </Button>
    </div>
  );
}

export default function CareerPlanSummaryCard({
  title,
  testId,
  updatedAt,
  loading,
  viewAllHref,
  showViewAll = true,
  footer,
  options = [],
  children
}: {
  title: string;
  testId: string;
  updatedAt?: string | null;
  loading: boolean;
  viewAllHref?: string;
  showViewAll?: boolean;
  footer?: {
    label: string;
    footerTestId: string;
    buttonTestId: string;
    ariaLabel?: string;
    onClick: () => void;
  };
  options?: SelectOptions[];
  children?: ReactNode;
}) {
  const navigate = useNavigate();
  const updatedLabel = updatedAt ? `Updated ${formatDate(updatedAt)}` : null;
  const actionButtons = footer ? (
    <HubCardFooter
      footerTestId={footer.footerTestId}
      buttonTestId={footer.buttonTestId}
      ariaLabel={footer.ariaLabel}
      onClick={footer.onClick}
    >
      {footer.label}
    </HubCardFooter>
  ) : showViewAll && viewAllHref ? (
    <HubCardFooter
      footerTestId={`${testId}-view-footer`}
      buttonTestId={`${testId}-view-all`}
      ariaLabel={`View ${title}`}
      onClick={() => navigate(viewAllHref)}
    >
      View
    </HubCardFooter>
  ) : undefined;

  return (
    <div className="h-full min-w-0 w-full" data-testid={testId}>
      {loading ? (
        <CardSkeleton className="w-full" width={100} rows={5} />
      ) : (
        <DetailsCard heading={title} className="!items-stretch" actionButtons={actionButtons}>
          <div className="flex min-h-0 w-full flex-1 flex-col">
            {updatedLabel ? (
              <p
                className="-mt-1 mb-4 text-body-medium text-sys-brand-on-surface-variant"
                data-testid="details-card-caption"
              >
                {updatedLabel}
              </p>
            ) : null}
            {options.length > 0 ? (
              <div className="flex w-full min-w-0 max-w-full flex-col">
                {options.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <ListItem {...item} iconBackground />
                  </div>
                ))}
              </div>
            ) : null}
            {children}
          </div>
        </DetailsCard>
      )}
    </div>
  );
}
